<script setup lang="ts">
import { useToastStore } from '@/stores/ui';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from '@lucide/vue';

const toastStore = useToastStore();

const STYLES: Record<string, { border: string; icon: string; bar: string; halo: string }> = {
  success: { border: 'border-l-emerald-500', icon: 'text-emerald-500', bar: 'bg-emerald-500', halo: 'bg-emerald-50 dark:bg-emerald-500/10' },
  error: { border: 'border-l-red-500', icon: 'text-red-500', bar: 'bg-red-500', halo: 'bg-red-50 dark:bg-red-500/10' },
  info: { border: 'border-l-domika-500', icon: 'text-domika-500', bar: 'bg-domika-500', halo: 'bg-domika-50 dark:bg-domika-500/10' },
  warning: { border: 'border-l-warn-500', icon: 'text-warn-500', bar: 'bg-warn-500', halo: 'bg-warn-50 dark:bg-warn-500/10' },
};
const ICONS: Record<string, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
};
</script>

<template>
  <div
    class="pointer-events-none fixed inset-x-0 top-4 z-50 flex flex-col items-center gap-2 px-4 sm:items-end sm:right-4 sm:left-auto"
    aria-live="polite"
    role="status"
  >
    <transition-group name="toast" tag="div" class="flex w-full max-w-sm flex-col gap-2">
      <div
        v-for="toast in toastStore.toasts"
        :key="toast.id"
        class="toast-card card pointer-events-auto relative flex items-start gap-2.5 overflow-hidden border-l-4 px-4 py-3 text-sm shadow-lg"
        :class="STYLES[toast.type].border"
      >
        <span class="toast-icon mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full" :class="STYLES[toast.type].halo">
          <component :is="ICONS[toast.type]" :size="16" :class="STYLES[toast.type].icon" aria-hidden="true" />
        </span>
        <span class="flex-1 text-slate-700 dark:text-slate-200">{{ toast.message }}</span>
        <button
          type="button"
          class="shrink-0 rounded-md p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          aria-label="Cerrar notificación"
          @click="toastStore.dismiss(toast.id)"
        >
          <X :size="15" />
        </button>
        <!-- Tiempo que le queda en pantalla -->
        <span
          class="toast-timer absolute bottom-0 left-0 h-0.5 w-full origin-left opacity-60"
          :class="STYLES[toast.type].bar"
          :style="{ animationDuration: `${toast.duration}ms` }"
          aria-hidden="true"
        />
      </div>
    </transition-group>
  </div>
</template>

<style scoped>
.toast-timer {
  animation: toast-timer linear forwards;
}
.toast-icon {
  animation: toast-icon 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both 0.1s;
}
@keyframes toast-timer {
  from { transform: scaleX(1); }
  to { transform: scaleX(0); }
}
@keyframes toast-icon {
  from { transform: scale(0) rotate(-45deg); }
  to { transform: scale(1) rotate(0); }
}
</style>
